import { Tooltip } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

export const WarehouseTooltip = ({
  children,
  warehouse,
  productUrl,
  showTooltip,
}) => {
  if (!showTooltip) {
    return children;
  }

  const tooltipContent = (
    <div>
      <span style={{ color: "#373737" }}>
        This is the closest warehouse with inventory.
      </span>
      <br />
      <a
        href={productUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: "#373737",
          textDecoration: "underline",
          marginTop: "4px",
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
        }}
      >
        See Full Availability
        <OpenInNewIcon sx={{ fontSize: 16, color: "#373737" }} />
      </a>
    </div>
  );

  return (
    <Tooltip
      enterDelay={500}
      componentsProps={{
        tooltip: {
          sx: {
            background: "#f9f9f9",
            opacity: "0.6",
            fontSize: "14px",
            color: "#373737",
            marginTop: "6px !important",
            boxShadow:
              "0 15px 35px rgba(50, 50, 93, 0.1), 0 5px 15px rgba(0, 0, 0, 0.07)",
            border: "1px solid darkgray",
          },
        },
        arrow: {
          sx: {
            color: "#f9f9f9",
            borderBottom: "none",
            "::before": {
              border: "1px solid darkgray",
            },
          },
        },
      }}
      title={tooltipContent}
      arrow={true}
    >
      {children}
    </Tooltip>
  );
};

export const PriceTooltip = ({
  children,
  productUrl,
  showTooltip,
  priceDifference,
}) => {
  if (!showTooltip) {
    return children;
  }

  // Ensure consistent formatting for display regardless of input type
  let displayPrice;
  if (priceDifference === null || priceDifference === undefined) {
    displayPrice = "0.00";
  } else if (typeof priceDifference === 'string') {
    // Try to parse the string as a number
    const parsed = parseFloat(priceDifference);
    displayPrice = isNaN(parsed) ? "0.00" : parsed.toFixed(2);
  } else if (typeof priceDifference === 'number') {
    displayPrice = isNaN(priceDifference) ? "0.00" : priceDifference.toFixed(2);
  } else {
    // Fallback for unexpected types
    displayPrice = "0.00";
  }

  const tooltipContent = (
    <div>
      <span style={{ color: "#373737" }}>
        This is ${displayPrice} more than the lowest option.
      </span>
      <br />
      <a
        href={productUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: "#373737",
          textDecoration: "underline",
          marginTop: "4px",
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
        }}
      >
        See All Price Options
        <OpenInNewIcon sx={{ fontSize: 16, color: "#373737" }} />
      </a>
    </div>
  );

  return (
    <Tooltip
      enterDelay={500}
      componentsProps={{
        tooltip: {
          sx: {
            background: "#f9f9f9",
            opacity: "0.6",
            fontSize: "14px",
            color: "#373737",
            marginTop: "6px !important",
            boxShadow:
              "0 15px 35px rgba(50, 50, 93, 0.1), 0 5px 15px rgba(0, 0, 0, 0.07)",
            border: "1px solid darkgray",
          },
        },
        arrow: {
          sx: {
            color: "#f9f9f9",
            borderBottom: "none",
            "::before": {
              border: "1px solid darkgray",
            },
          },
        },
      }}
      title={tooltipContent}
      arrow={true}
    >
      <div>{children}</div>
    </Tooltip>
  );
};

export const StatusTooltip = ({ children, status, icon: IconComponent }) => {
  const tooltipContent = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <IconComponent sx={{ fontSize: 16, color: 'rgba(0, 0, 0, 0.87)' }} />
      {status}
    </div>
  );

  return (
    <Tooltip 
      title={tooltipContent}
      enterDelay={500}
      leaveDelay={0}
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'white',
            color: 'rgba(0, 0, 0, 0.87)',
            fontSize: '0.875rem',
            border: '1px solid rgba(0, 0, 0, 0.12)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            padding: '8px 12px',
            maxWidth: 'none'
          }
        }
      }}
    >
      {children}
    </Tooltip>
  );
};

export const StockTooltip = ({ children, productUrl }) => {
  const tooltipContent = (
    <div>
      <span style={{ color: "#373737" }}>
        This item is not in stock with any of your preferred suppliers.
      </span>
      <br />
      <a
        href={productUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: "#373737",
          textDecoration: "underline",
          marginTop: "4px",
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
        }}
      >
        See Full Availability
        <OpenInNewIcon sx={{ fontSize: 16, color: "#373737" }} />
      </a>
    </div>
  );
  
  return (
    <Tooltip
      enterDelay={500}
      componentsProps={{
        tooltip: {
          sx: {
            background: "#f9f9f9",
            opacity: "0.6",
            fontSize: "14px",
            color: "#373737",
            marginTop: "6px !important",
            boxShadow:
              "0 15px 35px rgba(50, 50, 93, 0.1), 0 5px 15px rgba(0, 0, 0, 0.07)",
            border: "1px solid darkgray",
          },
        },
        arrow: {
          sx: {
            color: "#f9f9f9",
            borderBottom: "none",
            "::before": {
              border: "1px solid darkgray",
            },
          },
        },
      }}
      title={tooltipContent}
      arrow={true}
    >
      {children}
    </Tooltip>
  );
};

export const InvoiceNumberTooltip = ({ children }) => {
  const tooltipContent = (
    <div>
      <span style={{ color: "#373737" }}>
        Tag items by invoice number to identify them in your carts
      </span>
    </div>
  );
  
  return (
    <Tooltip
      enterDelay={500}
      componentsProps={{
        tooltip: {
          sx: {
            background: "#f9f9f9",
            opacity: "0.6",
            fontSize: "14px",
            color: "#373737",
            marginTop: "6px !important",
            boxShadow:
              "0 15px 35px rgba(50, 50, 93, 0.1), 0 5px 15px rgba(0, 0, 0, 0.07)",
            border: "1px solid darkgray",
          },
        },
        arrow: {
          sx: {
            color: "#f9f9f9",
            borderBottom: "none",
            "::before": {
              border: "1px solid darkgray",
            },
          },
        },
      }}
      title={tooltipContent}
      arrow={true}
    >
      {children}
    </Tooltip>
  );
};

export const SizeNotFoundTooltip = ({ children, size }) => {
  const tooltipContent = (
    <div>
      <span style={{ color: "#373737" }}>
        The size "{size}" cannot be found for this style or color.
      </span>
    </div>
  );

  return (
    <Tooltip
      enterDelay={500}
      componentsProps={{
        tooltip: {
          sx: {
            background: "#f9f9f9",
            opacity: "0.6",
            fontSize: "14px",
            color: "#373737",
            marginTop: "6px !important",
            boxShadow:
              "0 15px 35px rgba(50, 50, 93, 0.1), 0 5px 15px rgba(0, 0, 0, 0.07)",
            border: "1px solid darkgray",
          },
        },
        arrow: {
          sx: {
            color: "#f9f9f9",
            borderBottom: "none",
            "::before": {
              border: "1px solid darkgray",
            },
          },
        },
      }}
      title={tooltipContent}
      arrow={true}
    >
      {children}
    </Tooltip>
  );
};

export const ItemNotFoundTooltip = ({ children }) => {
  const tooltipContent = (
    <div>
      <span style={{ color: "#373737" }}>
        We were unable to match this to an item in the DGI catalog. 
        Please update the style using the <BorderColorIcon sx={{ fontSize: 16, verticalAlign: 'text-bottom' }} /> icon and try again.
      </span>
    </div>
  );

  return (
    <Tooltip
      enterDelay={500}
      componentsProps={{
        tooltip: {
          sx: {
            background: "#f9f9f9",
            opacity: "0.6",
            fontSize: "14px",
            color: "#373737",
            marginTop: "6px !important",
            boxShadow:
              "0 15px 35px rgba(50, 50, 93, 0.1), 0 5px 15px rgba(0, 0, 0, 0.07)",
            border: "1px solid darkgray",
          },
        },
        arrow: {
          sx: {
            color: "#f9f9f9",
            borderBottom: "none",
            "::before": {
              border: "1px solid darkgray",
            },
          },
        },
      }}
      title={tooltipContent}
      arrow={true}
    >
      {children}
    </Tooltip>
  );
};

// New tooltip for fixed position items in invoices
export const FixedItemsTooltip = ({ children }) => {
  const tooltipContent = (
    <div>
      <span style={{ color: "#373737" }}>
        Some items in this invoice are in your actual cart and can't be moved in the Cart Composer.
      </span>
    </div>
  );

  return (
    <Tooltip
      enterDelay={500}
      placement="top"
      componentsProps={{
        tooltip: {
          sx: {
            background: "#f9f9f9",
            opacity: "0.6",
            fontSize: "14px",
            color: "#373737",
            marginBottom: "6px !important",
            boxShadow:
              "0 15px 35px rgba(50, 50, 93, 0.1), 0 5px 15px rgba(0, 0, 0, 0.07)",
            border: "1px solid darkgray",
          },
        },
        arrow: {
          sx: {
            color: "#f9f9f9",
            borderBottom: "none",
            "::before": {
              border: "1px solid darkgray",
            },
          },
        },
      }}
      title={tooltipContent}
      arrow={true}
    >
      {children}
    </Tooltip>
  );
};

export const StyleMismatchTooltip = ({ children, isAcknowledged }) => {
  const tooltipContent = isAcknowledged 
    ? "Style mismatch acknowledged" 
    : "Acknowledge potential style mismatch";

    return (
      <Tooltip
        enterDelay={500}
        placement="top"
        componentsProps={{
          tooltip: {
            sx: {
              background: "#f9f9f9",
              opacity: "0.6",
              fontSize: "14px",
              color: "#373737",
              marginBottom: "6px !important",
              boxShadow:
                "0 15px 35px rgba(50, 50, 93, 0.1), 0 5px 15px rgba(0, 0, 0, 0.07)",
              border: "1px solid darkgray",
            },
          },
          arrow: {
            sx: {
              color: "#f9f9f9",
              borderBottom: "none",
              "::before": {
                border: "1px solid darkgray",
              },
            },
          },
        }}
        title={tooltipContent}
        arrow={true}
      >
        {children}
    </Tooltip>
  );
};

export const UnacknowledgedItemTooltip = ({ children }) => {
  const tooltipContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <div>This item has a potential match but needs acknowledgement.</div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        Acknowledge this match using the <CheckCircleOutlineIcon sx={{ fontSize: 16, mx: 1 }} /> on the right side.
      </div>
    </div>
  );

  return (
    <Tooltip 
      title={tooltipContent}
      enterDelay={500}
      leaveDelay={0}
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'white',
            color: 'rgba(0, 0, 0, 0.87)',
            fontSize: '0.875rem',
            border: '1px solid rgba(0, 0, 0, 0.12)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            padding: '8px 12px',
            maxWidth: 'none'
          }
        }
      }}
    >
      {children}
    </Tooltip>
  );
};

export const InvoiceSelectionTooltip = ({ children }) => {
  const tooltipContent = (
    <div>
      <span style={{ color: "#373737" }}>
        Only selected invoices will be added to Cart Composer
      </span>
    </div>
  );

  return (
    <Tooltip
      enterDelay={500}
      componentsProps={{
        tooltip: {
          sx: {
            background: "#f9f9f9",
            opacity: "0.6",
            fontSize: "14px",
            color: "#373737",
            marginTop: "6px !important",
            boxShadow:
              "0 15px 35px rgba(50, 50, 93, 0.1), 0 5px 15px rgba(0, 0, 0, 0.07)",
            border: "1px solid darkgray",
          },
        },
        arrow: {
          sx: {
            color: "#f9f9f9",
            borderBottom: "none",
            "::before": {
              border: "1px solid darkgray",
            },
          },
        },
      }}
      title={tooltipContent}
      arrow={true}
    >
      {children}
    </Tooltip>
  );
};
